import type { SellableStudioPlanId } from 'src/routes/settings/plans/components/studio/studioPlanFeatures';
import type {
  StudioAppUsageResponse,
  StudioPlanId,
} from 'src/services/api/studioCreditApi';

/**
 * The app-allowance rules the client still owns. The allowance, the current
 * count and the per-app fee are all the server's (`/studio-app-usage`,
 * `/studio-extra-apps-preview`) — the same count billing meters against — so
 * what is left here is the upgrade ladder, which has no server equivalent.
 *
 * Kept free of React, redux and API imports so it stays unit-testable on its
 * own — `usePlanStatus` reaches `src/utils` and therefore `axios`, which needs
 * `window`.
 */

/**
 * Whether the workspace has run out of room. Only `studio` workspaces carry an
 * allowance: Classic and unsubscribed ones are exempt, and an unresolved read
 * fails OPEN, because wrongly blocking a build is far worse than wrongly
 * allowing one — the server enforces the same allowance regardless.
 */
export const isUsageAtLimit = (
  usage: StudioAppUsageResponse | undefined,
): boolean =>
  usage?.family === 'studio' && usage.countableApps >= usage.includedApps;

/**
 * The plan pitched when a plan runs out of room, or undefined at the top of the
 * ladder where there is nothing left to sell. Free is pitched Pro directly rather
 * than stepping through Starter — the design's "Upgrade to Pro", and the target
 * the sidebar upgrade card already uses.
 */
const UPGRADE_TARGET: Record<StudioPlanId, SellableStudioPlanId | undefined> = {
  freemium: 'professional',
  starter: 'professional',
  professional: 'advanced',
  advanced: undefined,
};

export const getUpgradeTarget = (
  plan: StudioPlanId,
): SellableStudioPlanId | undefined => UPGRADE_TARGET[plan];
